import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/** Density ramps — swap on energy bands for variety. */
const RAMPS = [
  ' .:-=+*#%@',
  ' .\'`^":;Il!i><~+_-?][}{1)(|/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$',
  ' ·•◦○●◉◎',
  ' ▁▂▃▄▅▆▇█',
  ' ⠁⠂⠄⡀⢀⠠⠐⠈⣿',
];

const GLYPHS_BEAT = ['♪', '♫', '♬', '♩', '♯', '♭', '※', '✦', '✧', '★'];

export const asciiViz: Visualizer = {
  id: 'ascii',
  label: 'ASCII Art',
  draw(frame) {
    const { ctx, w, h, freq, wave, bands, t } = frame;
    clearBg(ctx, w, h, 1);
    ctx.fillStyle = '#03060a';
    ctx.fillRect(0, 0, w, h);

    const cols = Math.min(72, Math.max(40, Math.floor(w / 14)));
    const rows = Math.min(32, Math.max(16, Math.floor(h / 20)));
    const cellW = w / cols;
    const cellH = h / rows;
    const fontSize = Math.max(9, Math.floor(Math.min(cellW, cellH) * 0.88));
    ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // pick ramp by energy regime (changes over song sections)
    const rampIdx =
      bands.kick > 0.55
        ? 3
        : bands.highs > 0.45
          ? 2
          : bands.bass > 0.5
            ? 4
            : Math.floor((t * 0.08 + bands.energy * 2) % 3);
    const RAMP = RAMPS[rampIdx] ?? RAMPS[0]!;

    const cx = (cols - 1) / 2;
    const cy = (rows - 1) / 2;
    // slow rotation of sampling domain
    const ang = t * (0.15 + bands.mids * 0.35);
    const cosA = Math.cos(ang);
    const sinA = Math.sin(ang);
    // radial pulse
    const pulse = 0.85 + bands.bass * 0.35 + bands.kick * 0.4;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // normalize centered coords
        let nx = (c - cx) / cols;
        let ny = (r - cy) / rows;
        // rotate sample space
        const rx = nx * cosA - ny * sinA;
        const ry = nx * sinA + ny * cosA;
        const dist = Math.hypot(nx, ny) * pulse;

        // multi-source field — not a flat scrolling grid
        const binA = Math.min(
          freq.length - 1,
          Math.floor(Math.abs(rx + 0.5) * (freq.length - 1)),
        );
        const binB = Math.min(
          freq.length - 1,
          Math.floor(Math.abs(ry + 0.5) * (freq.length * 0.7)),
        );
        const fA = freq[binA]! / 255;
        const fB = freq[binB]! / 255;

        // waveform ring / horizontal slice
        const wi = Math.min(
          wave.length - 1,
          Math.floor(((Math.atan2(ny, nx) / Math.PI + 1) * 0.5) * (wave.length - 1)),
        );
        const wv = Math.abs(wave[wi] || 0);
        const waveRing = Math.exp(-Math.abs(dist - (0.25 + bands.energy * 0.2)) * 8) * wv * 1.6;

        // plasma-ish interference
        const plasma =
          0.5 +
          0.5 *
            Math.sin(rx * 12 + t * (1.5 + bands.energy) + fA * 4) *
            Math.cos(ry * 10 - t * 1.2 + fB * 3);

        let v =
          fA * 0.45 +
          fB * 0.25 +
          waveRing * 0.55 +
          plasma * 0.2 * (0.4 + bands.mids) +
          (1 - Math.min(1, dist * 1.6)) * bands.energy * 0.2;

        // beat sparkles at lattice points
        if (bands.beat > 0.35 && ((c + r + Math.floor(t * 8)) % 7 === 0)) {
          v += bands.beat * 0.45;
        }

        v = Math.min(1, Math.max(0, v * (0.7 + bands.energy * 0.55)));
        if (v < 0.06) continue;

        const x = (c + 0.5) * cellW;
        const y = (r + 0.5) * cellH;

        let ch: string;
        if (bands.kick > 0.5 && v > 0.7 && (c + r) % 5 === 0) {
          ch = GLYPHS_BEAT[(c + Math.floor(t * 6)) % GLYPHS_BEAT.length]!;
        } else {
          const ci = Math.min(RAMP.length - 1, Math.floor(v * (RAMP.length - 1)));
          ch = RAMP[ci]!;
        }

        const hue = 150 + dist * 80 + fA * 40 + bands.highs * 30 + rampIdx * 12;
        const light = 32 + v * 48;
        ctx.fillStyle = `hsla(${hue}, 90%, ${light}%, ${0.3 + v * 0.7})`;
        // rare glow only on peaks (cheap)
        if (v > 0.85) {
          ctx.shadowColor = `hsla(${hue}, 100%, 55%, 0.4)`;
          ctx.shadowBlur = 4;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.fillText(ch, x, y);
      }
    }
    ctx.shadowBlur = 0;

    // big center waveform as ASCII underline
    ctx.font = `600 ${Math.max(10, Math.round(w * 0.013))}px ui-monospace, monospace`;
    ctx.textAlign = 'left';
    ctx.fillStyle = `rgba(86,240,200,${0.4 + bands.energy * 0.4})`;
    const mode = ['DOT', 'CLASSIC', 'BUBBLE', 'BARS', 'BRAILLE'][rampIdx] ?? 'ASCII';
    ctx.fillText(
      `// ${mode}  e:${bands.energy.toFixed(2)}  k:${bands.kick.toFixed(2)}  b:${bands.bass.toFixed(2)}`,
      12,
      h - 14,
    );

    drawHud(frame);
  },
};
